import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch,
  FiCopy,
  FiCheck,
  FiZap,
  FiClock,
  FiRefreshCw,
  FiAlertTriangle
} from 'react-icons/fi';
import { useRecipes } from '../../hooks/useRecipes';
import { GridSearchBar, GridPagination } from './GridShared';
import MacSubscribePrompt from './MacSubscribePrompt';
import { getMacSsoToken } from '../../services/workflowService';
import type { RecipeApp } from '../../services/recipeService';
import './RecipesGrid.css';

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  draft: '#6b7280',
  stopped: '#6b7280',
  processing: '#f59e0b',
  failed: '#ef4444'
};

function getStatusColor(status: string): string {
  return STATUS_COLORS[status] ?? '#6b7280';
}

// ── Recipe apps strip ─────────────────────────────────────────────────────────

const MAX_VISIBLE = 4;

const RecipeAppsStrip: React.FC<{ apps: RecipeApp[] }> = ({ apps }) => {
  const sorted = [...apps].sort((a, b) => a.order - b.order);
  const visible = sorted.slice(0, MAX_VISIBLE);
  const overflow = sorted.length - MAX_VISIBLE;

  return (
    <div className='recipe-card__apps-strip'>
      {visible.map((ra, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className='recipe-card__apps-arrow'>›</span>}
          <div
            className={`recipe-card__app-bubble${ra.app_type === 'if_condition' || ra.app_type === 'wait' ? ' recipe-card__app-bubble--special' : ''}`}
          >
            {ra.app?.icon?.url ? (
              <img src={ra.app.icon.url} alt={ra.app.name} />
            ) : ra.app_type === 'wait' ? (
              <FiClock size={13} />
            ) : (
              <FiZap size={13} />
            )}
          </div>
        </React.Fragment>
      ))}
      {overflow > 0 && (
        <span className='recipe-card__apps-overflow'>+{overflow}</span>
      )}
    </div>
  );
};

// ── RecipeCard (Global — with Clone button) ───────────────────────────────────

interface RecipeCardProps {
  recipe: {
    id: number;
    name: string;
    status: string;
    key: string;
    recipe_apps?: RecipeApp[];
  };
  isCloningKey: string | null;
  cloneSuccess: string | null;
  cloneError: string | null;
  onClone: (key: string) => void;
  notSubscribed?: boolean;
}

const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  isCloningKey,
  cloneSuccess,
  cloneError,
  onClone,
  notSubscribed
}) => {
  const [hovered, setHovered] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const statusColor = getStatusColor(recipe.status);
  const isCloning = isCloningKey === recipe.key;
  const isSuccess = cloneSuccess === recipe.key;
  const isError = cloneError === recipe.key;

  const handleSubscribe = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setSubscribing(true);
    try {
      const amsToken = localStorage.getItem('amsToken') ?? '';
      const ssoToken = await getMacSsoToken(amsToken);
      window.open(
        `${(process.env.REACT_APP_FUSIONNOW_APP_URL)}/now-certs-auth?token=${encodeURIComponent(ssoToken)}&redirectfrom=myamp&signup=true`,
        '_blank'
      );
    } finally {
      setSubscribing(false);
    }
  };

  let btnClass = 'recipe-card__btn';
  if (isSuccess) btnClass += ' recipe-card__btn--success';
  else if (isError) btnClass += ' recipe-card__btn--error';

  return (
    <motion.div
      className='recipe-card'
      whileHover={{ y: -3, scale: 1.01 }}
      transition={{ duration: 0.18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        ...(hovered && { border: `1px solid ${statusColor}40` }),
        boxShadow: hovered ? `0 0 18px ${statusColor}12` : 'none'
      }}
    >
      <div className='recipe-card__header'>
        <div
          className='recipe-card__icon'
          style={{ background: statusColor + '20', color: statusColor }}
        >
          <FiCopy size={18} />
        </div>
        <div className='recipe-card__title-wrap'>
          <h3 className='recipe-card__name'>{recipe.name}</h3>
          <span
            className='recipe-card__category'
            style={{
              background: statusColor + '18',
              border: `1px solid ${statusColor}30`,
              color: statusColor
            }}
          >
            {recipe.status}
          </span>
        </div>
      </div>

      {(recipe.recipe_apps?.length ?? 0) > 0 && (
        <RecipeAppsStrip apps={recipe.recipe_apps!} />
      )}

      <div className='recipe-card__footer'>
        <span className='recipe-card__steps'>
          {recipe.recipe_apps?.length ?? 0} step
          {(recipe.recipe_apps?.length ?? 0) !== 1 ? 's' : ''}
        </span>
        {notSubscribed ? (
          <button
            className='recipe-card__btn recipe-card__btn--subscribe'
            onClick={handleSubscribe}
            disabled={subscribing}
          >
            <FiZap size={11} />
            {subscribing ? 'Opening…' : 'Subscribe to Use'}
          </button>
        ) : (
          <button
            className={btnClass}
            onClick={e => {
              e.stopPropagation();
              onClone(recipe.key);
            }}
            disabled={isCloning || isSuccess}
          >
            {isCloning ? (
              <>
                <span className='recipe-card__btn-spinner' /> Cloning…
              </>
            ) : isSuccess ? (
              <>
                <FiCheck size={11} /> Cloned!
              </>
            ) : isError ? (
              'Failed'
            ) : (
              <>
                <FiCopy size={11} /> Use Recipe
              </>
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
};

// ── MyRecipeCard (My Recipes — with View In MAC button) ───────────────────────

interface MyRecipeCardProps {
  recipe: {
    id: number;
    name: string;
    status: string;
    key: string;
    recipe_apps?: RecipeApp[];
  };
  isViewingInMac: string | null;
  viewInMacError: string | null;
  onView: (key: string) => void;
}

const MyRecipeCard: React.FC<MyRecipeCardProps> = ({
  recipe,
  isViewingInMac,
  viewInMacError,
  onView
}) => {
  const [hovered, setHovered] = useState(false);
  const statusColor = getStatusColor(recipe.status);
  const isLoading = isViewingInMac === recipe.key;
  const isError = viewInMacError === recipe.key;

  let bg = 'rgba(139,92,246,0.10)';
  let border = 'rgba(139,92,246,0.25)';
  let color = '#8b5cf6';
  let label = 'View In MAC';

  if (isLoading) {
    bg = 'rgba(139,92,246,0.08)';
    label = 'Opening…';
  } else if (isError) {
    bg = 'rgba(239,68,68,0.12)';
    border = 'rgba(239,68,68,0.35)';
    color = '#ef4444';
    label = 'Failed';
  }

  return (
    <motion.div
      className='recipe-card'
      whileHover={{ y: -3, scale: 1.01 }}
      transition={{ duration: 0.18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        ...(hovered && { border: `1px solid ${statusColor}40` }),
        boxShadow: hovered ? `0 0 18px ${statusColor}12` : 'none'
      }}
    >
      <div className='recipe-card__header'>
        <div
          className='recipe-card__icon'
          style={{ background: statusColor + '20', color: statusColor }}
        >
          <FiZap size={18} />
        </div>
        <div className='recipe-card__title-wrap'>
          <h3 className='recipe-card__name'>{recipe.name}</h3>
          <span
            className='recipe-card__category'
            style={{
              background: statusColor + '18',
              border: `1px solid ${statusColor}30`,
              color: statusColor
            }}
          >
            {recipe.status}
          </span>
        </div>
      </div>

      {(recipe.recipe_apps?.length ?? 0) > 0 && (
        <RecipeAppsStrip apps={recipe.recipe_apps!} />
      )}

      <div className='recipe-card__footer'>
        <span className='recipe-card__steps'>
          {recipe.recipe_apps?.length ?? 0} step
          {(recipe.recipe_apps?.length ?? 0) !== 1 ? 's' : ''}
        </span>
        <motion.button
          onClick={e => {
            e.stopPropagation();
            onView(recipe.key);
          }}
          disabled={isLoading}
          whileHover={isLoading ? {} : { scale: 1.03 }}
          whileTap={isLoading ? {} : { scale: 0.97 }}
          className='recipe-card__btn'
          style={{
            background: bg,
            border: `1px solid ${border}`,
            color,
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? (
            <motion.span
              className='recipe-card__btn-spinner'
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
            >
              <FiRefreshCw size={11} />
            </motion.span>
          ) : (
            <FiZap size={11} />
          )}
          {label}
        </motion.button>
      </div>
    </motion.div>
  );
};

// ── RecipesGrid (exported) ────────────────────────────────────────────────────

const RecipesGrid: React.FC = () => {
  const {
    recipes,
    meta,
    isLoading,
    error,
    notSubscribed,
    myRecipes,
    myMeta,
    isLoadingMy,
    myError,
    searchQuery,
    setSearchQuery,
    isCloningKey,
    cloneSuccess,
    cloneError,
    fetchRecipes,
    fetchMyRecipes,
    goToPage,
    goToMyPage,
    cloneRecipe,
    isViewingInMac,
    viewInMacError,
    viewInMac
  } = useRecipes();

  const currentPage = meta?.current_page ?? 1;
  const totalPages = meta?.total_pages ?? 1;
  const totalCount = meta?.total_count ?? 0;

  const myCurrentPage = myMeta?.current_page ?? 1;
  const myTotalPages = myMeta?.total_pages ?? 1;
  const myTotalCount = myMeta?.total_count ?? 0;

  return (
    <div className='recipe-grid'>
      <GridSearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder='Search recipes...'
      />

      {/* ── Combined empty state ──────────────────────────────────────────────── */}
      {!isLoading &&
        !isLoadingMy &&
        recipes.length === 0 &&
        myRecipes.length === 0 &&
        !error &&
        !myError &&
        !notSubscribed && (
          <div className='mk-empty'>
            <FiSearch size={32} className='recipe-grid__empty-icon' />
            <p className='mk-empty__title'>
              {searchQuery
                ? `No recipes match "${searchQuery}"`
                : 'No recipes found'}
            </p>
            <p className='mk-empty__sub'>
              {searchQuery
                ? 'Try a different name or clear the search'
                : 'Check back later'}
            </p>
          </div>
        )}

      {/* ── Global Recipes section ──────────────────────────────────────────── */}
      {(isLoading || recipes.length > 0 || !!error) && (
        <>
          <div className='recipe-grid__section-title'>Global Recipes</div>

          {isLoading ? (
            <div className='mk-loading'>
              <div className='mk-loading__spinner' />
              Loading recipes…
            </div>
          ) : error ? (
            <div className='mk-error'>
              <FiAlertTriangle size={15} />
              <p className='mk-error__msg'>{error}</p>
              <button className='mk-error__retry' onClick={fetchRecipes}>
                <FiRefreshCw size={12} /> Retry
              </button>
            </div>
          ) : (
            <>
              <div className='recipe-grid__cards'>
                {recipes.map(recipe => (
                  <RecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    isCloningKey={isCloningKey}
                    cloneSuccess={cloneSuccess}
                    cloneError={cloneError}
                    onClone={cloneRecipe}
                    notSubscribed={notSubscribed}
                  />
                ))}
              </div>
              {!searchQuery && (
                <GridPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  label='recipes'
                  onPage={goToPage}
                />
              )}
            </>
          )}
        </>
      )}

      {/* ── My Recipes section ──────────────────────────────────────────────── */}
      {(isLoadingMy || myRecipes.length > 0 || !!myError || notSubscribed) && (
        <>
          <div className='recipe-grid__section-title recipe-grid__section-title--my'>
            My Recipes
          </div>

          {notSubscribed ? (
            <MacSubscribePrompt />
          ) : isLoadingMy ? (
            <div className='mk-loading'>
              <div className='mk-loading__spinner' />
              Loading my recipes…
            </div>
          ) : myError ? (
            <div className='mk-error'>
              <FiAlertTriangle size={15} />
              <p className='mk-error__msg'>{myError}</p>
              <button className='mk-error__retry' onClick={fetchMyRecipes}>
                <FiRefreshCw size={12} /> Retry
              </button>
            </div>
          ) : (
            <>
              <div className='recipe-grid__cards'>
                {myRecipes.map(recipe => (
                  <MyRecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    isViewingInMac={isViewingInMac}
                    viewInMacError={viewInMacError}
                    onView={viewInMac}
                  />
                ))}
              </div>
              {!searchQuery && (
                <GridPagination
                  currentPage={myCurrentPage}
                  totalPages={myTotalPages}
                  totalCount={myTotalCount}
                  label='recipes'
                  onPage={goToMyPage}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RecipesGrid;

